import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Card, Spinner, Row, Col, Tab, Tabs, Badge } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaBook, FaVideo, FaClipboardCheck, FaPlus, FaCog, FaList, FaFilePdf, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/Capacitaciones.css';
import CapacitacionCard from '../components/CapacitacionCard';

const API_URL = (process.env.REACT_APP_API_URL || "/").replace(/\/$/, '');

// Mapeo de roles
const ROLES_MAP = {
   0: 'Todos',
   1: 'Administración',
   2: 'Inspección',
   3: 'Taller',
   4: 'Conducción'
};

const CargarCapacitaciones = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('modulo');
  const [categorias, setCategorias] = useState([]);
  const [modulosCount, setModulosCount] = useState(0);
  const [loadingModulos, setLoadingModulos] = useState(false);
  const [loadingCategoria, setLoadingCategoria] = useState(false);
  
  const [nuevaCategoria, setNuevaCategoria] = useState({
    nombre: '',
    descripcion: '',
    activo: true,
    rol_destinado: '0'
  });

  const usuario = JSON.parse(localStorage.getItem('user'));

  const [form, setForm] = useState({
    categoria_id: '',
    titulo: '',
    orden: '',
    video_url: '',
    tipo_contenido: 'youtube',
    requiere_aprobacion: true
  });

  const [archivoFile, setArchivoFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargarCategorias = () => {
    const token = localStorage.getItem('token');
    
    fetch(`${API_URL}/api/capacitaciones/categorias`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) setCategorias(data.data);
      })
      .catch(() => toast.error('Error al cargar categorías'));
  };

  const toggleActivoCategoria = async (id, estadoActual) => {
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${API_URL}/api/capacitaciones/categorias/${id}/toggle-activo`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.mensaje);
        cargarCategorias(); // Recargar lista
      } else {
        toast.error(data.error || 'Error al cambiar estado');
      }
    } catch (error) {
      toast.error('Error al cambiar estado de la capacitación');
    }
  };

  useEffect(() => {
    cargarCategorias();
  }, []);

  const obtenerSiguienteModulo = async (categoriaId) => {
    if (!categoriaId) {
      setModulosCount(0);
      setForm(prev => ({ ...prev, orden: '' }));
      return;
    }

    setLoadingModulos(true);
    try {
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/capacitaciones/modulos/${categoriaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      
      if (data.success && Array.isArray(data.data)) {
        const siguienteNumero = data.data.length + 1;
        setModulosCount(data.data.length);
        setForm(prev => ({ ...prev, orden: siguienteNumero.toString() }));
      } else {
        setModulosCount(0);
        setForm(prev => ({ ...prev, orden: '1' }));
      }
    } catch {
      setForm(prev => ({ ...prev, orden: '1' }));
    } finally {
      setLoadingModulos(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'categoria_id') {
      setForm(prev => ({ ...prev, [name]: value }));
      obtenerSiguienteModulo(value);
    } else {
      setForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleNuevaCategoriaChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNuevaCategoria(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const crearCategoria = async () => {
    if (!nuevaCategoria.nombre.trim()) {
      toast.error('Ingresá un nombre para la capacitación');
      return;
    }

    setLoadingCategoria(true);
    try {
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/capacitaciones/categorias`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(nuevaCategoria)
      });

      const data = await res.json();
      if (!data.success) throw new Error('Error al crear capacitación');

      toast.success('✅ Capacitación creada correctamente');
      setNuevaCategoria({ nombre: '', descripcion: '', activo: true, rol_destinado: '0' });
      cargarCategorias();
      setActiveTab('gestion'); // Ir al listado después de crear
    } catch {
      toast.error('No se pudo crear la capacitación');
    } finally {
      setLoadingCategoria(false);
    }
  };

  const handleArchivoUpload = async (moduloId) => {
    const formData = new FormData();
    formData.append('archivo', archivoFile);

    const usuario = JSON.parse(localStorage.getItem('user'));
    const token = localStorage.getItem('token');

    const headers = {
      'Authorization': `Bearer ${token}`,
      'x-user-id': usuario?.CdEmpleado || usuario?.Legajo || '',
      'x-user-name': usuario?.Nombre || '',
      'x-user-role': String(usuario?.IdRol || ''),
      'x-base-id': usuario?.base_id || ''
    };

    const endpoint = `${API_URL}/api/capacitaciones/modulos/${moduloId}/archivo`;

    const res = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: formData
    });

    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Error al subir archivo');

    return { url: data.video_url, tipo: data.tipo_contenido };
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.categoria_id || !form.titulo || !form.orden) {
      toast.error('Completá todos los campos obligatorios');
      return;
    }

    // Validar que tenga al menos URL o archivo
    if (!form.video_url && !archivoFile) {
      toast.error('Debés ingresar una URL de YouTube o subir un archivo');
      return;
    }

    setLoading(true);

    try {
      let finalVideoUrl = form.video_url;
      let finalTipoContenido = form.tipo_contenido;

      // Primero crear el módulo
      const payload = {
        ...form,
        video_url: form.video_url || '', // Enviar la URL si existe
        tipo_contenido: form.tipo_contenido,
        subido_por: usuario?.Legajo
      };

      // Construir headers de usuario
      const token = localStorage.getItem('token');
      
      const usuarioHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': usuario?.CdEmpleado || usuario?.Legajo || '',
        'x-user-name': usuario?.Nombre || '',
        'x-user-role': String(usuario?.IdRol || ''),
        'x-base-id': usuario?.base_id || ''
      };

      const resModulo = await fetch(`${API_URL}/api/capacitaciones/modulos`, {
        method: 'POST',
        headers: usuarioHeaders,
        body: JSON.stringify(payload)
      });
      const dataModulo = await resModulo.json();
      if (!dataModulo.success || !dataModulo.id) throw new Error('Error al registrar módulo');
      const moduloId = dataModulo.id;

      // Si hay archivo, subirlo y actualizar el módulo
      if (archivoFile) {
        const resultado = await handleArchivoUpload(moduloId);
        finalVideoUrl = resultado.url;
        finalTipoContenido = resultado.tipo;

        // Actualizar el módulo con la URL y tipo
        await fetch(`${API_URL}/api/capacitaciones/modulos/${moduloId}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({
            video_url: finalVideoUrl,
            tipo_contenido: finalTipoContenido
          })
        });
      }

      toast.success('✅ Módulo creado correctamente');
      setForm({ categoria_id: '', titulo: '', orden: '', video_url: '', tipo_contenido: 'youtube', requiere_aprobacion: true });
      setArchivoFile(null);
      setModulosCount(0);
    } catch {
      toast.error('No se pudo crear el módulo');
    } finally {
      setLoading(false);
    }
  };

  const categoriaSeleccionada = categorias.find(c => c.id.toString() === form.categoria_id);

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="capacitaciones-header mb-4">
        <h2 className="mb-1">
          <FaBook className="me-2" />
          Gestión de Capacitaciones
        </h2>
        <p className="text-muted mb-0">Administrá módulos, capacitaciones y evaluaciones</p>
      </div>

      {/* Tabs principales */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="capacitaciones-tabs mb-4"
      >
        {/* TAB 1: Cargar Módulo */}
        <Tab 
          eventKey="modulo" 
          title={<><FaVideo className="me-2" />Cargar Módulo</>}
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="card-title-custom mb-4">
                <span className="title-icon bg-primary">
                  <FaVideo />
                </span>
                Agregar nuevo módulo
              </h5>

              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="form-label-custom">Capacitación</Form.Label>
                      <Form.Select 
                        name="categoria_id" 
                        value={form.categoria_id} 
                        onChange={handleChange} 
                        required
                        className="form-control-lg"
                      >
                        <option value="">Seleccionar capacitación...</option>
                        {categorias.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="form-label-custom">
                        N° de Módulo
                        {loadingModulos && <Spinner animation="border" size="sm" className="ms-2" />}
                      </Form.Label>
                      <Form.Control 
                        type="number" 
                        name="orden" 
                        value={form.orden} 
                        onChange={handleChange} 
                        required
                        className="form-control-lg"
                        min="1"
                        disabled={loadingModulos}
                      />
                      {modulosCount > 0 && (
                        <Form.Text className="text-muted">
                          {modulosCount} módulo(s) existente(s)
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {categoriaSeleccionada && (
                  <div className="alert alert-light border mt-3 mb-3">
                    <strong>{categoriaSeleccionada.nombre}</strong>
                    {categoriaSeleccionada.descripcion && (
                      <p className="mb-0 mt-1 small text-muted">{categoriaSeleccionada.descripcion}</p>
                    )}
                  </div>
                )}

                <Form.Group className="mt-3">
                  <Form.Label className="form-label-custom">Título del módulo</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="titulo" 
                    value={form.titulo} 
                    onChange={handleChange} 
                    required
                    placeholder="Ej: Introducción a la seguridad vial"
                  />
                </Form.Group>

                {/* Selector de tipo de contenido */}
                <Form.Group className="mt-3">
                  <Form.Label className="form-label-custom">Tipo de contenido</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="tipo-youtube"
                      name="tipo_contenido"
                      label={<><FaVideo className="me-1" /> Video de YouTube</>}
                      value="youtube"
                      checked={form.tipo_contenido === 'youtube'}
                      onChange={handleChange}
                    />
                    <Form.Check
                      type="radio"
                      id="tipo-video"
                      name="tipo_contenido"
                      label={<><FaVideo className="me-1" /> Video (subir archivo)</>}
                      value="video"
                      checked={form.tipo_contenido === 'video'}
                      onChange={handleChange}
                    />
                    <Form.Check
                      type="radio"
                      id="tipo-pdf"
                      name="tipo_contenido"
                      label={<><FaFilePdf className="me-1" /> PDF</>}
                      value="pdf"
                      checked={form.tipo_contenido === 'pdf'}
                      onChange={handleChange}
                    />
                  </div>
                </Form.Group>

                <Row className="mt-3 g-3">
                  {form.tipo_contenido === 'youtube' && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-custom">URL del video de YouTube</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="video_url" 
                          value={form.video_url} 
                          onChange={handleChange} 
                          placeholder="https://youtube.com/watch?v=..."
                        />
                      </Form.Group>
                    </Col>
                  )}
                  {form.tipo_contenido === 'video' && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-custom">Subir video</Form.Label>
                        <Form.Control 
                          type="file" 
                          accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                          onChange={(e) => setArchivoFile(e.target.files[0])}
                        />
                        <Form.Text className="text-muted">
                          Formatos aceptados: MP4, WebM, MOV, AVI (máx. 100MB)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  )}
                  {form.tipo_contenido === 'pdf' && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-custom">Subir PDF</Form.Label>
                        <Form.Control 
                          type="file" 
                          accept="application/pdf"
                          onChange={(e) => setArchivoFile(e.target.files[0])}
                        />
                        <Form.Text className="text-muted">
                          Formato PDF (máx. 100MB)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                <Form.Group className="mt-4">
                  <Form.Check
                    type="switch"
                    id="requiere_aprobacion"
                    label="Requiere evaluación para aprobar"
                    name="requiere_aprobacion"
                    checked={form.requiere_aprobacion}
                    onChange={handleChange}
                  />
                </Form.Group>

                <div className="d-flex gap-2 mt-4 pt-2">
                  <Button 
                    type="submit" 
                    className="btn-custom-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <><Spinner animation="border" size="sm" className="me-2" />Guardando...</>
                    ) : (
                      <><FaPlus className="me-2" />Crear Módulo</>
                    )}
                  </Button>
                  
                  {form.requiere_aprobacion && form.categoria_id && (
                    <Button 
                      className="btn-custom-outline"
                      onClick={() => navigate('/admin/capacitaciones/evaluacion')}
                    >
                      <FaClipboardCheck className="me-2" />
                      Cargar Evaluación
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 2: Nueva Capacitación */}
        <Tab 
          eventKey="capacitacion" 
          title={<><FaPlus className="me-2" />Nueva Capacitación</>}
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="card-title-custom mb-4">
                <span className="title-icon bg-success">
                  <FaBook />
                </span>
                Crear nueva capacitación
              </h5>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="form-label-custom">Nombre</Form.Label>
                    <Form.Control
                      type="text"
                      name="nombre"
                      value={nuevaCategoria.nombre}
                      onChange={handleNuevaCategoriaChange}
                      placeholder="Ej: Seguridad e Higiene"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="form-label-custom">Destinado a</Form.Label>
                    <Form.Select
                      name="rol_destinado"
                      value={nuevaCategoria.rol_destinado}
                      onChange={handleNuevaCategoriaChange}
                    >
                      <option value="0">Todos los roles</option>
                      <option value="1">Administración</option>
                      <option value="2">Inspección</option>
                      <option value="3">Taller</option>
                      <option value="4">Conducción</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mt-3">
                <Form.Label className="form-label-custom">Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="descripcion"
                  value={nuevaCategoria.descripcion}
                  onChange={handleNuevaCategoriaChange}
                  placeholder="Descripción breve de la capacitación..."
                />
              </Form.Group>

              <Form.Group className="mt-4">
                <Form.Check
                  type="switch"
                  id="activo"
                  label="Capacitación activa (visible para usuarios)"
                  name="activo"
                  checked={nuevaCategoria.activo}
                  onChange={handleNuevaCategoriaChange}
                />
              </Form.Group>

              <Button 
                className="btn-custom-success mt-4"
                onClick={crearCategoria}
                disabled={loadingCategoria}
              >
                {loadingCategoria ? (
                  <><Spinner animation="border" size="sm" className="me-2" />Creando...</>
                ) : (
                  <><FaPlus className="me-2" />Crear Capacitación</>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 3: Gestionar Capacitaciones */}
        <Tab 
          eventKey="gestion" 
          title={<><FaList className="me-2" />Ver Capacitaciones</>}
        >
          {/* Tabla de capacitaciones activas (estilo clásico) */}
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="card-title-custom mb-4">
                <span className="title-icon bg-info">
                  <FaCog />
                </span>
                Capacitaciones existentes
              </h5>
              {categorias.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FaBook size={48} className="mb-3 opacity-50" />
                  <p>No hay capacitaciones creadas aún</p>
                  <Button 
                    className="btn-custom-primary"
                    onClick={() => setActiveTab('capacitacion')}
                  >
                    <FaPlus className="me-2" />
                    Crear primera capacitación
                  </Button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table capacitaciones-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Descripción</th>
                        <th>Sector</th>
                        <th className="text-center">Estado</th>
                        <th className="text-center">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categorias.map(cat => (
                        <tr key={cat.id} style={{ opacity: cat.activo ? 1 : 0.6 }}>
                          <td className="fw-medium">{cat.nombre}</td>
                          <td className="text-muted">{cat.descripcion || '-'}</td>
                          <td>
                            <Badge bg="secondary" className="badge-sector">
                              {ROLES_MAP[cat.rol_destinado] || 'Todos'}
                            </Badge>
                          </td>
                          <td className="text-center">
                            {cat.activo ? (
                              <Badge bg="success">Activa</Badge>
                            ) : (
                              <Badge bg="secondary">Inactiva</Badge>
                            )}
                          </td>
                          <td className="text-center">
                            <Button
                              size="sm"
                              variant={cat.activo ? "outline-danger" : "outline-success"}
                              onClick={() => toggleActivoCategoria(cat.id, cat.activo)}
                              title={cat.activo ? "Desactivar capacitación" : "Activar capacitación"}
                            >
                              {cat.activo ? (
                                <><FaToggleOff className="me-1" /> Desactivar</>
                              ) : (
                                <><FaToggleOn className="me-1" /> Activar</>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 4: Evaluaciones */}
        <Tab 
          eventKey="evaluacion" 
          title={<><FaClipboardCheck className="me-2" />Evaluaciones</>}
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-5 text-center">
              <div className="evaluaciones-empty">
                <FaClipboardCheck size={56} className="text-warning mb-3" />
                <h5>Gestión de Evaluaciones</h5>
                <p className="text-muted mb-4">
                  Creá y administrá las evaluaciones para cada módulo de capacitación
                </p>
                <Button 
                  className="btn-custom-warning"
                  onClick={() => navigate('/administrador/cargar-evaluacion')}
                >
                  <FaClipboardCheck className="me-2" />
                  Ir a Cargar Evaluaciones
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      <ToastContainer position="bottom-right" />
    </Container>
  );
}

export default CargarCapacitaciones;